import React from 'react';
import { FmdGoodOutlined } from '@mui/icons-material';
import { Box, Button, InputAdornment, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { usePlacesWidget } from 'react-google-autocomplete';
import { FilterData, adjustDateToGmt } from './utils';

// NOTE: Make sure to add REACT_APP_GOOGLE_API_KEY to your .env.local file for local development
// You can find the key in 1Password
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const styles = {
  filterContainer: {
    bgcolor: '#131C7A0D',
    borderRadius: '16px',
    border: '1px solid #EEEEEE',
    padding: '30px',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    display: 'flex',
    marginTop: '30px',
  },
  innerContainer: {
    flexDirection: { xs: 'column', md: 'column', lg: 'row' },
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    gap: 4,
    paddingBottom: '30px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
    },
    '& input': {
      fontFamily: 'Urbanist',
      fontWeight: 700,
      fontSize: '16px',
    },
  },
  button: {
    borderRadius: '12px',
    height: '50px',
    px: { xs: '50px', md: '100px', lg: '200px' },
    bgcolor: '#131C7A',
    textTransform: 'none',
    ':hover': {
      bgcolor: '#2d39c0',
    },
    fontSize: '14px',
    fontWeight: '700',
    fontFamily: 'Urbanist',
  },
  headerText: {
    fontSize: '14px',
    fontWeight: '700',
    fontFamily: 'Urbanist',
  },
};

const DEFAULT_RADIUS = 50;

interface FilterSectionProps {
  onPress: (filterData: FilterData) => void;
  isLoading: boolean;
  clear: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  onPress,
  isLoading,
  clear,
}) => {
  const [filterData, setFilterData] = React.useState<FilterData>({
    pickupLatitude: 0,
    pickupLongitude: 0,
    pickupRadius: DEFAULT_RADIUS,
    pickupDate: adjustDateToGmt(new Date()).toISOString()?.split('T')[0],
    dropoffLatitude: 0,
    dropoffLongitude: 0,
    dropoffRadius: DEFAULT_RADIUS,
  });
  const [pickupLocation, setPickupLocation] = React.useState('');
  const [dropoffLocation, setDropoffLocation] = React.useState('');
  const [pickupRadius, setPickupRadius] = React.useState(DEFAULT_RADIUS);
  const [dropoffRadius, setDropoffRadius] = React.useState(DEFAULT_RADIUS);

  const onChangeDate = (date: Date | null) => {
    if (date && !isNaN(date.getTime())) {
      const formattedDate = adjustDateToGmt(date)?.toISOString()?.split('T')[0];
      setFilterData((prev) => ({
        ...prev,
        pickupDate: formattedDate,
      }));
    }
  };

  const onChangeDeadhead =
    (field: 'pickup' | 'dropoff') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);

      if (field === 'pickup') {
        setPickupRadius(value);
        setFilterData((prev) => ({
          ...prev,
          pickupRadius: value,
        }));
      } else {
        setDropoffRadius(value);
        setFilterData((prev) => ({
          ...prev,
          dropoffRadius: value,
        }));
      }
    };

  const onPlaceSelected = (place: any, field: 'pickup' | 'dropoff') => {
    const lat = place?.geometry?.location?.lat();
    const lng = place?.geometry?.location?.lng();

    if (field === 'pickup') {
      setPickupLocation(place?.formatted_address);
    } else {
      setDropoffLocation(place?.formatted_address);
    }

    setFilterData((prev) => ({
      ...prev,
      [`${field}Latitude`]: lat,
      [`${field}Longitude`]: lng,
    }));
  };

  const { ref: pickupInputRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: (place) => onPlaceSelected(place, 'pickup'),
    options: {
      componentRestrictions: { country: 'us' },
    },
  });

  const { ref: dropoffInputRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: (place) => onPlaceSelected(place, 'dropoff'),
    options: {
      componentRestrictions: { country: 'us' },
    },
  });

  const onSubmit = () => {
    onPress(filterData);
  };

  const ctaDisabled = React.useMemo(
    () =>
      isLoading ||
      !pickupLocation ||
      !dropoffLocation ||
      !pickupRadius ||
      !dropoffRadius ||
      !filterData.pickupDate ||
      !filterData.pickupLatitude ||
      !filterData.pickupLongitude ||
      !filterData.dropoffLatitude ||
      !filterData.dropoffLongitude,
    [
      dropoffLocation,
      dropoffRadius,
      filterData.dropoffLatitude,
      filterData.dropoffLongitude,
      filterData.pickupDate,
      filterData.pickupLatitude,
      filterData.pickupLongitude,
      isLoading,
      pickupLocation,
      pickupRadius,
    ]
  );

  React.useEffect(() => {
    setPickupLocation('');
    setDropoffLocation('');
    setPickupRadius(DEFAULT_RADIUS);
    setDropoffRadius(DEFAULT_RADIUS);
    setFilterData({
      pickupLatitude: 0,
      pickupLongitude: 0,
      pickupRadius: DEFAULT_RADIUS,
      pickupDate: adjustDateToGmt(new Date()).toISOString()?.split('T')[0],
      dropoffLatitude: 0,
      dropoffLongitude: 0,
      dropoffRadius: DEFAULT_RADIUS,
    });
  }, [clear]);

  return (
    <Box sx={styles.filterContainer}>
      <Box sx={styles.innerContainer}>
        {/* Pickup */}
        <Box>
          <p style={styles.headerText}>Pickup</p>
          <TextField
            fullWidth
            value={pickupLocation}
            onChange={(e) => setPickupLocation(e.target.value)}
            color="primary"
            placeholder="City, State"
            sx={styles.textField}
            inputRef={pickupInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FmdGoodOutlined />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Pickup Deadhead */}
        <Box>
          <p style={styles.headerText}>Deadhead (mi)</p>
          <TextField
            fullWidth
            variant="outlined"
            type="number"
            placeholder="0 miles"
            sx={styles.textField}
            onChange={onChangeDeadhead('pickup')}
            value={pickupRadius}
          />
        </Box>

        {/* Delivery */}
        <Box>
          <p style={styles.headerText}>Delivery</p>
          <TextField
            fullWidth
            value={dropoffLocation}
            onChange={(e) => setDropoffLocation(e.target.value)}
            color="primary"
            placeholder="City, State"
            sx={styles.textField}
            inputRef={dropoffInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FmdGoodOutlined />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Delivery Deadhead */}
        <Box>
          <p style={styles.headerText}>Deadhead (mi)</p>
          <TextField
            fullWidth
            variant="outlined"
            type="number"
            placeholder="0 miles"
            sx={styles.textField}
            onChange={onChangeDeadhead('dropoff')}
            value={dropoffRadius}
          />
        </Box>

        {/* Pickup Date */}
        <Box>
          <p style={styles.headerText}>Pickup Date</p>
          <DatePicker
            sx={styles.textField}
            onChange={onChangeDate}
            defaultValue={adjustDateToGmt(new Date())}
          />
        </Box>
      </Box>

      {/* Search Button */}
      <Box>
        <Button
          variant="contained"
          sx={styles.button}
          onClick={onSubmit}
          disabled={ctaDisabled}>
          Search Carriers
        </Button>
      </Box>
    </Box>
  );
};

export default FilterSection;
